// Prayer time utilities for Kota Malang
// API: https://equran.id/api/v2/shalat (Provinsi: Jawa Timur, Kota: Kota Malang)

export interface PrayerTime {
  name: string;
  arabicName: string;
  time: string; // HH:MM format
  iqomahTime: string; // HH:MM format
}

export interface PrayerTimesData {
  date: string;
  hijriDate: string;
  timings: {
    subuh: string;
    terbit: string;
    dzuhur: string;
    ashar: string;
    maghrib: string;
    isya: string;
  };
}

// Add minutes to a time string HH:MM
export function addMinutes(timeStr: string, minutes: number): string {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

// Parse time string to total minutes since midnight
export function timeToMinutes(timeStr: string): number {
  const clean = timeStr.split(' ')[0]; // remove timezone suffix
  const [h, m] = clean.split(':').map(Number);
  return h * 60 + m;
}

// Format current time in WIB
export function formatTimeWIB(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export function formatDateID(date: Date): string {
  const days = ['Ahad', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
  ];
  const day = days[date.getDay()];
  const d = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}, ${d} ${month} ${year}`;
}

// Fallback prayer times for Kota Malang (equran.id field names)
export const FALLBACK_TIMINGS = {
  subuh: '04:33',
  terbit: '05:51',
  dzuhur: '11:42',
  ashar: '14:59',
  maghrib: '17:31',
  isya: '18:44',
};

export const PRAYER_NAMES: Array<{
  key: keyof typeof FALLBACK_TIMINGS;
  name: string;
  arabicName: string;
  iqomahKey: keyof import('./storage').IqomahSettings;
}> = [
  { key: 'subuh',   name: 'Subuh',   arabicName: 'الفجر', iqomahKey: 'subuh' },
  { key: 'dzuhur',  name: 'Dzuhur',  arabicName: 'الظهر', iqomahKey: 'dzuhur' },
  { key: 'ashar',   name: 'Ashar',   arabicName: 'العصر', iqomahKey: 'ashar' },
  { key: 'maghrib', name: 'Maghrib', arabicName: 'المغرب', iqomahKey: 'maghrib' },
  { key: 'isya',    name: 'Isya',    arabicName: 'العشاء', iqomahKey: 'isya' },
];

// Hijri month names in Indonesian
export const HIJRI_MONTHS_ID = [
  'Muharram', 'Safar', "Rabi\'ul Awwal", "Rabi\'ul Akhir",
  'Jumadil Awwal', 'Jumadil Akhir', 'Rajab', "Sya\'ban",
  'Ramadhan', 'Syawwal', "Dzulqa\'dah", 'Dzulhijjah',
];